import React, { useContext, useEffect, useState } from "react";
import {
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { ConfirmationDialog } from "../../../common/ConfirmationDialog";
import {
  IGroupInfo,
  INonPatientChats,
  IPatientChats,
} from "@home-health-notify/domain";
import { IHideGroupRequest } from "@home-health-notify/models";
import { useSnackbar } from "notistack";
import Toggle from "../../../common/Toggle/Toggle";

type HideGroupToggleType = {
  groupInfo: IGroupInfo;
};

const HideGroupToggle = ({ groupInfo }: HideGroupToggleType) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const tNsSettings = (key: string) => t(key, { ns: "settings" });

  const [isHideGroupModalOpen, setIsHideGroupModalOpen] =
    useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(
    groupInfo?.settings?.hidden ?? false,
  );

  useEffect(() => {
    if (groupInfo?.settings) {
      setChecked(groupInfo.settings.hidden);
    }
  }, [groupInfo]);

  const closeHideGroupModal = (checked: boolean) => {
    setIsHideGroupModalOpen(false);
    setChecked(checked);
  };
  const openHideGroupModal = () => {
    setIsHideGroupModalOpen(true);
  };

  const getRequestPayload = () => {
    const requestPayload: IHideGroupRequest = {
      id: groupInfo.id,
      settings: {
        hidden: !checked,
      },
    };
    return requestPayload;
  };

  const onHideUnHideGroup = async () => {
    try {
      const chatManager =
        selectedOrgUser?.chatManager.getChatManagerForGroupType(
          groupInfo.groupType,
        ) as IPatientChats | INonPatientChats;
      await chatManager.hideUnHideChat(getRequestPayload());
      const successMessage = `${groupInfo.name} ${
        checked
          ? tNsSettings("unhideGroupSuccessMessage")
          : tNsSettings("hideGroupSuccessMessage")
      }`;
      enqueueSnackbar(successMessage, {
        autoHideDuration: 3000,
      });
      closeHideGroupModal(!checked);
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
      closeHideGroupModal(checked);
    }
  };

  const onToggle = () => {
    openHideGroupModal();
  };
  return (
    <>
      <ConfirmationDialog
        isOpen={isHideGroupModalOpen}
        onClose={() => closeHideGroupModal(checked)}
        confirmationTitle={
          checked ? tNsSettings("unhideChat") : tNsSettings("hideChat")
        }
        confirmationMessage={
          checked
            ? tNsSettings("confirmUnhideChat")
            : tNsSettings("confirmHideChat")
        }
        confirmationButtonText={
          checked ? tNsSettings("unhide") : tNsSettings("hide")
        }
        onConfirmationAction={onHideUnHideGroup}
        shouldCloseOnConfirm={false}
      />
      <Toggle label={tNs("hideGroup")} onToggle={onToggle} checked={checked} />
    </>
  );
};

export default HideGroupToggle;
